import React from 'react';
import FlatpickrImport from 'react-flatpickr';

// Vite's dev-server dependency pre-bundling sometimes fails to unwrap react-flatpickr's
// CJS `exports.default` (it's a UMD build, not a plain `module.exports = ...`), binding
// this import to the whole `{ default: Component }` exports object instead of the
// component itself — "Element type is invalid... got: object". Unwrap defensively so this
// works whether or not that interop quirk is present (prod's Rollup build isn't affected).
const Flatpickr = (FlatpickrImport as unknown as { default?: typeof FlatpickrImport }).default ?? FlatpickrImport;
import { useAppContext, globalValue } from '../../apps/AppContext';
import { DateFormats } from '../../../types';
import { startOfDayInBizTZ as toStartOfDayInBizTZ } from '../../utils/MiscFunctions';

// Inverse of toStartOfDayInBizTZ: converts a biz-tz-midnight Date back to browser-local
// midnight so flatpickr (which renders in browser-local time) shows the correct calendar date.
const bizTZToLocalMidnight = (date: Date): Date => {
  const timezone = globalValue?.bizMaster?.bizDetails?.timeZone
    || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateStr = date.toLocaleDateString('en-CA', { timeZone: timezone }); // "YYYY-MM-DD"
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

interface FlatPickrWrapperProps {
  value?: Date | string;
  onChange: (date: Date) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const dateFormatMap: Record<DateFormats, string> = {
  [DateFormats["MMMM d, yyyy"]]: 'F j, Y',
  [DateFormats["d MMMM, yyyy"]]: 'j F, Y',
  [DateFormats["yyyy-MM-dd"]]: 'Y-m-d',
  [DateFormats["MM/dd/yyyy"]]: 'm/d/Y',
  [DateFormats["dd. MM. yyyy"]]: 'd. m. Y',
  [DateFormats["dd-MM-yyyy"]]: 'd-m-Y',
};

const DatePicker: React.FC<FlatPickrWrapperProps> = ({
  value,
  onChange,
  placeholder = 'Select a date'.toTranslatedDisplay(),
  disabled,
  className = 'form-control',
}) => {
  const { bizMaster } = useAppContext();
  const selectedFormat = bizMaster?.bizDetails?.dateFormat as DateFormats || DateFormats['dd-MM-yyyy'];
  const flatpickrFormat = dateFormatMap[selectedFormat] || 'Y-m-d';
  const [isCalendarChange, setIsCalendarChange] = React.useState(true);
  // Create a format-aware date parser
  const parseDate = (inputValue: string): Date | null => {
    if (!inputValue || inputValue.trim() === '') return null;

    // Try to parse using the expected format first
    const trimmedInput = inputValue.trim();

    // Handle different format patterns
    switch (selectedFormat) {
      case DateFormats["dd-MM-yyyy"]:
        const ddMMyyyy = /^(\d{1,2})[-.\/](\d{1,2})[-.\/](\d{4})$/.exec(trimmedInput);
        if (ddMMyyyy) {
          const day = parseInt(ddMMyyyy[1], 10);
          const month = parseInt(ddMMyyyy[2], 10) - 1; // Month is 0-indexed
          const year = parseInt(ddMMyyyy[3], 10);
          return new Date(year, month, day);
        }
        break;

      case DateFormats["MM/dd/yyyy"]:
        const MMddyyyy = /^(\d{1,2})[-.\/](\d{1,2})[-.\/](\d{4})$/.exec(trimmedInput);
        if (MMddyyyy) {
          const month = parseInt(MMddyyyy[1], 10) - 1; // Month is 0-indexed
          const day = parseInt(MMddyyyy[2], 10);
          const year = parseInt(MMddyyyy[3], 10);
          return new Date(year, month, day);
        }
        break;

      case DateFormats["yyyy-MM-dd"]:
        const yyyyMMdd = /^(\d{4})[-.\/](\d{1,2})[-.\/](\d{1,2})$/.exec(trimmedInput);
        if (yyyyMMdd) {
          const year = parseInt(yyyyMMdd[1], 10);
          const month = parseInt(yyyyMMdd[2], 10) - 1; // Month is 0-indexed
          const day = parseInt(yyyyMMdd[3], 10);
          return new Date(year, month, day);
        }
        break;

      case DateFormats["dd. MM. yyyy"]:
        const ddMMyyyy2 = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/.exec(trimmedInput);
        if (ddMMyyyy2) {
          const day = parseInt(ddMMyyyy2[1], 10);
          const month = parseInt(ddMMyyyy2[2], 10) - 1; // Month is 0-indexed
          const year = parseInt(ddMMyyyy2[3], 10);
          return new Date(year, month, day);
        }
        break;
    }

    // Fallback: ISO date-only string typed by the user (e.g. "2026-01-01").
    // new Date("YYYY-MM-DD") creates UTC midnight, which for UTC+ browsers means
    // getFullYear/Month/Date return the previous day. Parse the components directly
    // so the result is always local midnight matching the typed date.
    const isoOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmedInput);
    if (isoOnly) {
      const parsed = new Date(Number(isoOnly[1]), Number(isoOnly[2]) - 1, Number(isoOnly[3]));
      if (!isNaN(parsed.getTime())) return parsed;
    }

    return null;
  };

  const handleManualInput = (inputValue: string) => {
    const parsedDate = parseDate(inputValue);
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      onChange(toStartOfDayInBizTZ(parsedDate));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue?.trim() && !isCalendarChange) {
      setTimeout(() => handleManualInput(inputValue), 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setIsCalendarChange(false)
    if (e.key === 'Enter') {
      handleManualInput((e.target as HTMLInputElement).value);
    }
  };

  const displayValue = React.useMemo(() => {
    if (!value) return undefined;
    const d = value instanceof Date ? value : new Date(value as string);
    return isNaN(d.getTime()) ? undefined : bizTZToLocalMidnight(d);
  }, [value]);

  return (
    <Flatpickr
      value={displayValue}
      onChange={(dates: Date[]) => {
        setIsCalendarChange(true)
        if (dates.length > 0) {
          onChange(toStartOfDayInBizTZ(dates[0]));
        }
      }}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      disabled={disabled}
      options={{
        dateFormat: flatpickrFormat,
        allowInput: true,
        clickOpens: true,
        enableTime: false,
        altInput: false,
        wrap: false,
        // Remove the custom parseDate to avoid conflicts with calendar selection
      }}
      placeholder={placeholder}
    />
  );
};

export default DatePicker;